import React, { useState, useRef, useEffect } from 'react';
import '../styles/aichatbox.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AIChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Load conversation history from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('wennze_chat_history');
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    } else {
      // Add welcome message if no history
      setMessages([{
        role: 'assistant',
        content: "Bonjour! 👋 Je suis l'assistant virtuel Wennze. Comment puis-je vous aider aujourd'hui?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('wennze_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    // Add user message to chat
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Prepare conversation history for API (last 5 exchanges)
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(`${API_URL}/api/ai-support/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputValue,
          conversation_history: conversationHistory
        }),
      });

      const data = await response.json();

      if (data.success && data.response) {
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Fallback error message
        const errorMessage = {
          role: 'assistant',
          content: data.response || "Désolé, une erreur s'est produite. Veuillez réessayer.",
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "Désolé, je ne peux pas me connecter au serveur en ce moment. Veuillez réessayer plus tard ou contacter support@wennze.com",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    const confirmClear = window.confirm('Êtes-vous sûr de vouloir effacer la conversation?');
    if (confirmClear) {
      setMessages([{
        role: 'assistant',
        content: "Bonjour! 👋 Je suis l'assistant virtuel Wennze. Comment puis-je vous aider aujourd'hui?",
        timestamp: new Date().toISOString()
      }]);
      localStorage.removeItem('wennze_chat_history');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating chat button */}
      <button 
        className={`ai-chat-button ${isOpen ? 'hidden' : ''}`}
        onClick={handleToggle}
        aria-label="Ouvrir le chat d'assistance"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.2L4 17.2V4H20V16Z" fill="white"/>
          <circle cx="12" cy="10" r="1" fill="white"/>
          <circle cx="8" cy="10" r="1" fill="white"/>
          <circle cx="16" cy="10" r="1" fill="white"/>
        </svg>
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="ai-chatbox">
          {/* Header */}
          <div className="ai-chatbox-header">
            <div className="ai-chatbox-header-content">
              <div className="ai-chatbox-avatar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z" fill="white"/>
                </svg>
              </div>
              <div className="ai-chatbox-title">
                <h3>Assistant Wennze</h3>
                <span className="ai-chatbox-status">En ligne</span>
              </div>
            </div>
            <div className="ai-chatbox-actions">
              <button 
                className="ai-chatbox-action-btn"
                onClick={handleClearChat}
                aria-label="Effacer la conversation"
                title="Effacer la conversation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="white"/>
                </svg>
              </button>
              <button 
                className="ai-chatbox-close"
                onClick={handleToggle}
                aria-label="Fermer le chat"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="white"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="ai-chatbox-messages">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`ai-message ${message.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
              >
                <div className="ai-message-content">
                  {message.content}
                </div>
                <div className="ai-message-time">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="ai-message ai-message-assistant">
                <div className="ai-message-content ai-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="ai-chatbox-input">
            <input
              type="text"
              placeholder="Posez votre question..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button 
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Envoyer le message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatBox;
